import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { getComputerMenuQueryKey, COMPUTER_MENU_QUERY_PATH, DEFAULT_COMPUTER_MENU_LIMIT, } from '#pplx/frontend/apps/ask/queries/query-keys/computerKeys.js';
import { useInfiniteQuery } from '#pplx/frontend/react-query/useInfiniteQuery.js';
import { computerClient } from '#pplx_generated/typespec/rest/computer/client.js';
import { infiniteQueryOptions, keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
const COMPUTER_MENU_STALE_TIME_MS = 5 * 60 * 1000;
const COMPUTER_MENU_GC_TIME_MS = 10 * 60 * 1000;
export async function getComputerMenu({ collectionUuid, cursor, limit = DEFAULT_COMPUTER_MENU_LIMIT, query, sideChatAvailable = false, }, reason, headers) {
    const { data, error, response } = await computerClient.GET(COMPUTER_MENU_QUERY_PATH, reason, {
        params: {
            query: {
                collection_uuid: collectionUuid,
                cursor,
                limit,
                query,
                side_chat_available: sideChatAvailable,
                // Surfaces without a ForkCommandContext drop the item client-side.
                fork_available: true,
            },
        },
        headers,
    });
    if (error || !data) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to fetch computer menu',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
}
export function getComputerMenuQueryOptions({ collectionUuid, enabled = true, limit = DEFAULT_COMPUTER_MENU_LIMIT, locale, query, reason, sideChatAvailable = false, }) {
    const headers = locale
        ? { 'accept-language': locale }
        : undefined;
    return infiniteQueryOptions({
        queryKey: getComputerMenuQueryKey(limit, collectionUuid, locale, query, sideChatAvailable),
        queryFn: ({ pageParam }) => getComputerMenu({
            collectionUuid,
            cursor: pageParam,
            limit,
            query,
            sideChatAvailable,
        }, reason, headers),
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
        initialPageParam: null,
        enabled,
        staleTime: COMPUTER_MENU_STALE_TIME_MS,
        gcTime: COMPUTER_MENU_GC_TIME_MS,
    });
}
export function useComputerMenu({ collectionUuid, enabled = true, limit = DEFAULT_COMPUTER_MENU_LIMIT, query, sideChatAvailable = false, }) {
    const { locale } = useIntl();
    const menuQuery = useInfiniteQuery({
        ...getComputerMenuQueryOptions({
            collectionUuid,
            enabled,
            limit,
            locale,
            query,
            reason: 'use-computer-menu-hook',
            sideChatAvailable,
        }),
        placeholderData: keepPreviousData,
    });
    const featuredItems = useMemo(() => menuQuery.data?.pages[0]?.featured_items ?? [], [menuQuery.data]);
    const commandItems = useMemo(() => menuQuery.data?.pages[0]?.command_items ?? [], [menuQuery.data]);
    const skills = useMemo(() => menuQuery.data?.pages.flatMap((page) => page.skills) ?? [], [menuQuery.data]);
    const pluginItems = useMemo(() => menuQuery.data?.pages[0]?.plugin_items ?? [], [menuQuery.data]);
    return useMemo(() => ({
        featuredItems,
        commandItems,
        skills,
        pluginItems,
        isLoading: menuQuery.isLoading,
        isPlaceholderData: menuQuery.isPlaceholderData,
        isFetchingSearch: menuQuery.isRefetching,
        isFetchingNextPage: menuQuery.isFetchingNextPage,
        hasNextPage: menuQuery.hasNextPage ?? false,
        fetchNextPage: menuQuery.fetchNextPage,
        error: menuQuery.error,
    }), [
        featuredItems,
        commandItems,
        skills,
        pluginItems,
        menuQuery.isLoading,
        menuQuery.isPlaceholderData,
        menuQuery.isRefetching,
        menuQuery.isFetchingNextPage,
        menuQuery.hasNextPage,
        menuQuery.fetchNextPage,
        menuQuery.error,
    ]);
}
//# sourceMappingURL=useComputerMenu.js.map